import eventbus from 'tiny-emitter/instance';
// lodash methods
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import invokeMap from 'lodash/invokeMap';
import forEach from 'lodash/forEach';
import without from 'lodash/without';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import first from 'lodash/first';
import reject from 'lodash/reject';
import partition from 'lodash/partition';
// app-specific
import store from '@/store/store';
import {
  TYPE,
  LAYOUT,
  TABLE,
  SEAT,
  MODE,
  RENDERER_MODE,
  ZOOM,
  SECTION,
  EVENTSTATUS,
  SEATSTATUS,
  CHANNEL,
} from '@/constants';
import History from './utils/history';
import Selection from './models/selection';
import { Block } from './models/block';
import { Row } from './models/row';
// import Seat from './models/seat';
import ShapeHelper from './models/shapes/index';
import SectionHelper from './models/sections/index';
import { GAHelper } from './models/ga/index';
import fromJSON, { Clonable, Unit } from './models/index';
import { sortUnitsByDOMPosition, checkSeatForGap } from './utils/index';
import { WatchStopHandle, nextTick, reactive, watch } from 'vue';
import { useActions, useState } from '@/store/useStore';
import SVG from 'svg.js';
import { Seat } from './models/seat';
import { TableSeat } from './models/tables/tableSeat';
import { PredefinedShape } from './models/predefined/predefined';
import { Text } from './models/text';
import { getUnitsInSection } from './drawing/utils';
import RectangularTable from './models/tables/rectangularTable';
import RoundedTable from './models/tables/roundedTable';
import Application from './application';
import { Section } from './models/sections/section';
import { Table } from './models/tables/table';
import { POS } from '@/store/modules/pos';
import { Shape } from './models/shapes/shape';
import { GA } from './models/ga/ga';
import Model from './models/model';
import Polygon from './models/shapes/polygon';
import PolygonalSection from './models/sections/polygon';
import Point from './Point';
import { GroupBy } from './typeHelper';
import CircleSection from './models/sections/circle';
import RectangularSection from './models/sections/rect';
import Rect from './models/shapes/rect';
import { createCompas } from './drawing/compas';

const filterBySid = <T extends Table | GA | Seat | Block>(units: Unit[], sid: string) =>
  units.filter((u): u is T => u.sid === sid);

const filterByCid = (units: (Table | Seat)[], cid: number) =>
  units.filter((u) => 'categoryId' in u && u.categoryId === cid);

type Payload = {
  checkerBoardOrder: any;
  spaceBetweenGroup: number | undefined;
  groupSize: number | undefined;
  reverse: boolean | undefined;
};

type BgImageState = {
  bgImageScale: number;
  bgImageOpacity: number;
  bgImageShowInRenderer: boolean;
  isBgUploaded: boolean;
  x: number;
  y: number;
  transform: SVG.Transform;
};

export type State = {
  sections: Section[];
  blocks: Block[];
  units: Exclude<Unit, Row | TableSeat | Seat>[];
  bgImage: BgImageState | undefined;
  stagepin?: string;
};

export default class Seatmap {
  state?: State;
  blocks: Block[] = [];
  seats: Seat[] = [];
  units: Unit[] = [];
  sections: Section[] = [];
  stagepin: PredefinedShape | undefined;
  groupIdCounter = Number(new Date());
  orders = [];
  hotkeysDisabled = false;
  bulkSelected: Unit[] = [];
  zoomForSeatmap = 1;
  zoomMin = 1;
  zoomMax = 1;
  zoomForSection = 1;
  zoomForSeat = 1;
  scale = 1;
  lastUsedSeatSpacing = SECTION.DEFAULT_SEAT_SPACING;
  lastUsedRowSpacing = SECTION.DEFAULT_ROW_SPACING;
  selection = new Selection();
  svgRect = document.createElementNS('http://www.w3.org/2000/svg', 'svg').createSVGRect();

  store = reactive(useState('seatmap'));
  actions = useActions('seatmap');
  settings = reactive(useState('settings'));

  watchers: WatchStopHandle[] = [];
  history?: History;

  seatLimitCall: number | undefined = undefined;
  selectedZone: Section | null = null;
  currentZoomLevel: number | null = null;

  // added by selectionBox.js
  declare oldSelectionBox: SVG.Box;

  constructor(public app: Application) {
    this.initHistory();
    this.addEventListeners();
  }

  get seatsAndTables() {
    return [...this.seats, ...this.tables];
  }

  get tables() {
    return this.units.filter((u): u is Table => u.type === TYPE.TABLE);
  }

  get tableSeats() {
    return this.tables.flatMap((table) => table.seats);
  }

  get seatsAndTableSeats() {
    return [...this.seats, ...this.tableSeats];
  }

  get gaList() {
    return this.units.filter((u): u is GA => u.type === TYPE.GA);
  }

  get tablesAndGa() {
    return this.units.filter((u): u is Table | GA => u.type === TYPE.TABLE || u.type === TYPE.GA);
  }

  get seatsAndTablesAndGa() {
    return [...this.seats, ...this.tablesAndGa];
  }

  get bgImageShowInRenderer() {
    return store.state.seatmap.bgImageShowInRenderer;
  }

  get isBgUploaded() {
    return store.state.seatmap.isBgUploaded;
  }

  get currentMode() {
    return store.state.settings.mode;
  }

  get gapEnabled() {
    return this.app.props.isGapAvoidance;
  }

  getSectionById(sid: string) {
    return this.sections.find((s) => s.uuid === sid);
  }

  getSectionsByCategoryId(cid: number) {
    return this.sections.filter((s) => s.attributes.categoryId == cid);
  }

  unitsFromSection(sid: string) {
    return filterBySid([...this.blocks, ...this.units], sid);
  }

  predefinedsFromSection(sid: string) {
    return this.units.filter((u): u is PredefinedShape => u.sid === sid && u.type === TYPE.PREDEFINED);
  }

  seatsFromSection(sid: string) {
    return filterBySid<Seat>(this.seats, sid);
  }

  seatsAndTablesFromSection(sid: string) {
    return this.seatsAndTablesAndGa.filter((s) => s.sid === sid);
  }

  addEventListeners() {
    // selection events
    eventbus.on('selection:multiclick', this.onMultiClick, this);
    eventbus.on('selection:cleared', this.clearSelection, this);
    eventbus.on('selection:remove', this.removeSelectedUnits, this);
    eventbus.on('selection:clone', this.cloneSelection, this);
    eventbus.on('selection:started', this.handleSelectionStarted, this);
    eventbus.on('selection:finished', this.handleSelectionFinished, this);
    eventbus.on('selection:group-size', this.handleSelectionGroupDisable, this);
    eventbus.on('selection:group-space', this.handleSelectionGroupDisable, this);
    // seatmap
    eventbus.on('ga:capacity', this.handleGaCapacityChange, this);
    eventbus.on('block:removed', this.onRemoveBlock, this);
    eventbus.on('seat:removed', this.onRemoveSeat, this);
    eventbus.on('section:removed', this.onRemoveSection, this);
    eventbus.on('unit:removed', this.onRemoveUnit, this);
    eventbus.on('group:mouseEnter', this.onGroupHover, this);
    eventbus.on('group:mouseLeave', this.onGroupLeave, this);
    eventbus.on('group:createGroup', this.groupSeats, this);
    // history
    eventbus.on('history:pause', this.pauseHistory, this);
    eventbus.on('history:resume', this.resumeHistory, this);
    eventbus.on('history:undo', this.undoHistory, this);
    eventbus.on('history:redo', this.redoHistory, this);
    eventbus.on('history:jump', this.jumpToHistory, this);
    eventbus.on('history:snapshot', this.snapshot, this);
    //sections
    eventbus.on('section:units:show', this.showSectionUnits, this);
    eventbus.on('section:units:hide', this.hideSectionUnits, this);
    eventbus.on('section:units:lockPositionsAndShowInFront', this.lockPositionsAndShowInFront, this);
    eventbus.on('section:units:unlockPositionsAndHideInBack', this.unlockPositionsAndHideInBack, this);
    eventbus.on('section:category:assign', this.assignCategoryToSection, this);
    // eventbus.on('section:category:unassign', this.unassignCategoryFromSection, this);
    eventbus.on('section:pos:assign', this.assignPosToSection, this);
    eventbus.on('section:pos:unassign', this.unassignPosFromSection, this);
    eventbus.on('section:drag:start', this.onSectionDragStart, this);
    eventbus.on('section:drag:end', this.onSectionDragEnd, this);
    eventbus.on('section:rotate:start', this.onSectionRotateStart, this);
    eventbus.on('section:rotate:end', this.onSectionRotateEnd, this);
    eventbus.on('section:unfill', this.onSectionUnFill, this);
    eventbus.on('section:fill', this.onSectionFill, this);
    // global handlers
    eventbus.on('menu:reinit', this.reInitMenu, this);
    eventbus.on('hotkeys:disable', this.disableHotkeys, this);
    eventbus.on('seatmap:lock:show', this.showLockOnSeats, this);
    eventbus.on('seatmap:lock:hide', this.hideLockOnSeats, this);

    eventbus.on('seatmap:removeBlocks', this.removeBlocks, this);
    // eventbus.on('seatmap:labels:show', this.showLabels, this);
    // eventbus.on('seatmap:labels:hide', this.hideLabels, this);
    // camera
    if (RENDERER_MODE) {
      eventbus.on('camera:panned', this.updateVisibility, this);
    }

    // update scaling when it changes
    const { scale } = useState('camera');
    const { showHiddenSeats } = useState('seatmap');
    const { mode } = useState('settings');

    this.watchers = [
      watch(scale, this.onScaleChange.bind(this), { immediate: true }),
      watch(showHiddenSeats, this.updateSeatVisibility.bind(this)),
      watch(mode, () => this.updateSeatVisibility(showHiddenSeats.value)),
    ];
  }

  removeEventListeners() {
    // selection events
    eventbus.off('selection:multiclick', this.onMultiClick);
    eventbus.off('selection:cleared', this.clearSelection);
    eventbus.off('selection:remove', this.removeSelectedUnits);
    eventbus.off('selection:clone', this.cloneSelection);
    eventbus.off('selection:started', this.handleSelectionStarted);
    eventbus.off('selection:finished', this.handleSelectionFinished);
    // seatmap
    eventbus.off('ga:capacity', this.handleGaCapacityChange);
    eventbus.off('block:removed', this.onRemoveBlock);
    eventbus.off('seat:removed', this.onRemoveSeat);
    eventbus.off('section:removed', this.onRemoveSection);
    eventbus.off('unit:removed', this.onRemoveUnit);
    eventbus.off('group:mouseEnter', this.onGroupHover);
    eventbus.off('group:mouseLeave', this.onGroupLeave);
    // history
    eventbus.off('history:pause', this.pauseHistory);
    eventbus.off('history:resume', this.resumeHistory);
    eventbus.off('history:undo', this.undoHistory);
    eventbus.off('history:redo', this.redoHistory);
    eventbus.off('history:jump', this.jumpToHistory);
    eventbus.off('history:snapshot', this.snapshot);
    //sections
    eventbus.off('section:units:show', this.showSectionUnits);
    eventbus.off('section:units:hide', this.hideSectionUnits);
    eventbus.off('section:units:lockPositionsAndShowInFront', this.lockPositionsAndShowInFront);
    eventbus.off('section:units:unlockPositionsAndHideInBack', this.unlockPositionsAndHideInBack);
    eventbus.off('section:category:assign', this.assignCategoryToSection);
    // eventbus.off('section:category:unassign', this.unassignCategoryFromSection);
    eventbus.off('section:pos:assign', this.assignPosToSection);
    eventbus.off('section:pos:unassign', this.unassignPosFromSection);
    eventbus.off('section:drag:start', this.onSectionDragStart);
    eventbus.off('section:drag:end', this.onSectionDragEnd);
    eventbus.off('section:rotate:start', this.onSectionRotateStart);
    eventbus.off('section:rotate:end', this.onSectionRotateEnd);
    eventbus.off('section:unfill', this.onSectionUnFill);
    eventbus.off('section:fill', this.onSectionFill);
    // global handlers
    eventbus.off('menu:reinit', this.reInitMenu);
    eventbus.off('hotkeys:disable', this.disableHotkeys);
    eventbus.off('seatmap:lock:show', this.showLockOnSeats);
    eventbus.off('seatmap:lock:hide', this.hideLockOnSeats);
    eventbus.off('seatmap:removeBlocks', this.removeBlocks);
    // eventbus.off('seatmap:labels:show', this.showLabels);
    // eventbus.off('seatmap:labels:hide', this.hideLabels);
    // camera
    // if (RENDERER_MODE) {
    //   eventbus.off('camera:zoomed', this.updateVisibility);
    //   eventbus.off('canvas:moved', this.updateVisibility);
    // }

    if (this.watchers) {
      this.watchers.forEach((unwatch) => unwatch());
    }
  }

  onMultiClick(count: number) {
    if (this.selection.items.length > 0) {
      const unit = this.selection.first!;
      if (unit.type === TYPE.SEAT) {
        count === 2 ? this.selectRow(unit.row) : this.selectBlock(unit.block);
        this.reInitMenu(true);
      }
    }
  }

  pauseHistory() {
    this.history?.pause();
  }

  resumeHistory() {
    this.history?.resume();
  }

  reInitMenu(forced = false) {
    const currentSelectionBoxString = JSON.stringify(this.selection.rbox());
    const oldSelectionBoxString = JSON.stringify(this.oldSelectionBox);
    eventbus.emit('selection:updated', this.selection);
    if (currentSelectionBoxString !== oldSelectionBoxString || forced) {
      this.hideContextMenu();
      setTimeout(() => this.showContextMenu(), 1);
    }
  }

  moveSection(section: Section, dx: number, dy: number) {
    section.onDragStart();
    section.onDragMove(dx, dy);
    section.onDragEnd(dx, dy, true);
  }

  cloneSelection() {
    const clonedUnits: Unit[] = [];
    const clonedSeats: Seat[] = [];
    this.pauseHistory();
    const [seats, other] = this.selection.partitionedUnits;
    if (seats.length) {
      const clonedBlocks = this.cloneSelectedSeats(seats, { ts: new Date().getTime() });
      forEach(clonedBlocks, (block) => {
        this.addBlock(block, false);
        block.show();
        clonedSeats.push(...block.seats);
      });
    }
    forEach(other, (unit) => {
      let clonedUnitTemp: Clonable;
      if (unit.type === TYPE.SECTION) {
        clonedUnitTemp = this.cloneUnit<Section>(unit, { keepPosition: true });
        const cloneOptions = {
          sid: clonedUnitTemp.uuid,
          keepPosition: true,
          ts: new Date().getTime(),
        };
        const allSectionUnits = [...this.seats, ...this.units].filter((u) => u.sid === unit.uuid) as Clonable[];
        const [sectionSeats, sectionUnits] = partition(
          allSectionUnits,
          (item): item is Seat => item.type === TYPE.SEAT
        );
        if (sectionSeats.length) {
          const clonedSectionBlocks = this.cloneSelectedSeats(sectionSeats, cloneOptions);
          forEach(clonedSectionBlocks, (clonedBlock) => {
            this.addBlock(clonedBlock, false);
          });
        }
        forEach(sectionUnits, (sectionUnit) => {
          const clonedSectionUnit = this.cloneUnit(sectionUnit, cloneOptions);
          clonedSectionUnit.sid = clonedUnitTemp.uuid;
        });
        this.hideSectionUnits(clonedUnitTemp.uuid);
        this.moveSection(clonedUnitTemp as Section, 100, 100);
      } else {
        clonedUnitTemp = this.cloneUnit(unit as Clonable);
      }
      clonedUnits.push(clonedUnitTemp);
    });

    this.selectUnits([...clonedUnits, ...clonedSeats]);
    this.resumeHistory();
    this.reInitMenu(true);
    eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
    this.snapshot('Duplicate selection');
  }

  cloneUnit<T extends Clonable>(
    unit: T,
    options: { keepPosition?: boolean; keepLabel?: boolean; ts?: number } = { keepPosition: false, keepLabel: false }
  ) {
    let clonedUnit;
    switch (unit.type) {
      case TYPE.SHAPE:
        clonedUnit = ShapeHelper.clone(unit, options);
        break;
      case TYPE.GA:
        clonedUnit = GAHelper.clone(unit, options);
        break;
      case TYPE.SECTION:
        clonedUnit = SectionHelper.clone(
          unit as unknown as PolygonalSection | RectangularSection | CircleSection,
          options
        );
        break;
      default:
        clonedUnit = unit.clone(options);
        break;
    }
    this.addUnit(clonedUnit);
    return clonedUnit;
  }

  cloneSelectedSeats(
    seats: Seat[],
    {
      keepPosition = false,
      sid,
      ts,
      allowedStatus = [SEATSTATUS.FREE, SEATSTATUS.DISABLED, SEATSTATUS.LOCKED],
    }: {
      keepPosition?: boolean;
      sid?: string;
      ts?: number;
      allowedStatus?: (typeof SEATSTATUS)[keyof typeof SEATSTATUS][];
    } = {
      keepPosition: false,
      allowedStatus: [SEATSTATUS.FREE, SEATSTATUS.DISABLED, SEATSTATUS.LOCKED],
    }
  ) {
    const clonedBlocks: Block[] = [];
    const groupedBlocks = groupBy(seats, (seat) => seat.block.uuid);
    forEach(groupedBlocks, (gblock) => {
      const block = Block.create({ sid: sid || gblock[0].sid });
      const groupedRows = groupBy(gblock, (seat) => seat.row.uuid);
      forEach(groupedRows, (grow) => {
        const { initialLength, initialSeatsCount } = first(grow)!.row;
        const attributes = { initialLength, initialSeatsCount, label: SEAT.LABEL };
        const row = Row.create({ block, attributes });
        forEach(grow, (item) => {
          const exportedSeat = item.clone({ keepPosition, ts, allowedStatus });
          exportedSeat.row = row;
          row.addSeat(exportedSeat);
        });
        row.renderLabel();
      });
      clonedBlocks.push(block);
    });
    return clonedBlocks;
  }

  static isUnitInside(points: [number, number][], point: [number, number]) {
    const x = point[0];
    const y = point[1];
    let i = 0;
    let j = 0;
    let xi = 0;
    let xj = 0;
    let yi = 0;
    let yj = 0;
    let inside = false;
    let intersect = false;
    for (i = 0, j = points.length - 1; i < points.length; j = i++) {
      xi = points[i][0];
      yi = points[i][1];
      xj = points[j][0];
      yj = points[j][1];

      intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  updatePolygonPoints(polygon: SVG.Polygon) {
    const points = polygon.array().value as unknown as [number, number][];
    const newpoints = [];
    const svgRoot = this.app.svg.node as unknown as SVGSVGElement;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const pt = svgRoot.createSVGPoint();
      pt.x = points[i][0];
      pt.y = points[i][1];

      //Convert the global point into the space of the object you are dragging:
      const globalToLocal = this.app.getTransformToElement(svgRoot, polygon.node as unknown as SVGPolygonElement);
      const inObjectSpace = pt.matrixTransform(globalToLocal);

      const point = [];
      point[0] = inObjectSpace.x;
      point[1] = inObjectSpace.y;

      newpoints.push(point);
    }
    return newpoints;
  }

  addUnitFromDrawing(unit: Unit, select = true) {
    if (this.app.layout === LAYOUT.INSIDESECTION) {
      //Get new points afte a section is moved to new localtion
      //#1123 - Allow drawing outside a section for now
      //const points = this.updatePolygonPoints(this.app.currentSection.shape);
      //const isUnitInside = Seatmap.isUnitInside(points, unit.attributes.position);
      const isUnitInside = true;
      if (isUnitInside) {
        this.addUnit(unit);
        if (select) this.selectUnits(unit);
        eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
      } else {
        unit.destroy();
      }
    } else {
      this.addUnit(unit);
      if (select) this.selectUnits(unit);
      eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
    }
  }

  addBlock(block: Block, select = true, onInit = false) {
    this.history!.pause();
    this.blocks.push(block);
    if (this.app.layout === LAYOUT.INSIDESECTION) {
      forEach(block.seats, (seat) => {
        //Get new points afte a zone is moved to new localtion
        seat.attributes.position[0] += SEAT.INNER_RADIUS;
        seat.attributes.position[1] += SEAT.INNER_RADIUS;
        //#1123 - Allow drawing outside a zone for now
        //const points = this.updatePolygonPoints(this.app.currentSection.shape);
        //let isUnitInside = Seatmap.isUnitInside(points, seat.attributes.position);
        const isUnitInside = true;
        if (isUnitInside) {
          seat.attributes.position[0] -= SEAT.INNER_RADIUS;
          seat.attributes.position[1] -= SEAT.INNER_RADIUS;
          //isUnitInside = Seatmap.isUnitInside(points, seat.attributes.position);
        }
        if (isUnitInside) {
          this.addUnit(seat);
        } else {
          seat.destroy();
        }
      });
    } else {
      forEach(block.seats, (seat) => {
        this.addUnit(seat);
      });
    }
    if (block.rows && block.rows.length > 0) {
      if (select) this.selectBlock(block);
      this.history?.resume();
      if (!RENDERER_MODE) {
        eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
        this.snapshot(`Added block ${block.rows.length}x${block.rows[0].seats.length}`, onInit);
      }
    }
  }

  addUnit(unit: Unit, onInit = false) {
    switch (unit.type) {
      case TYPE.SEAT:
        this.seats.push(unit);
        break;
      case TYPE.SECTION:
        this.sections.push(unit);
        break;
      default:
        this.units.push(unit);
        this.units.filter((u) => u.type === TYPE.TEXT || u.type === TYPE.PREDEFINED).forEach((u) => u.view.front());
        break;
    }
    this.snapshot(`Added ${unit.type}`, onInit);
  }

  // why is this here? get rid of it or make it more common
  handleGaCapacityChange() {
    eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
    this.snapshot('GA capacity changed');
  }

  handleSelectionStarted() {
    if (!RENDERER_MODE) {
      this.clearSelection();
    }
  }

  handleSelectionGroupDisable(payload: Payload) {
    const rows: (Seat | TableSeat)[][] = [[]];
    let rowCounter = 0;
    const items = this.selection.items as (Seat | TableSeat)[];

    let rowId = ('row' in items[0] && items[0].row.uuid) || ('table' in items[0] && items[0].table.uuid);

    const hasRectangularTable = (item: any): item is { table: RectangularTable } =>
      'table' in item && item.table.subtype == TYPE.TABLE_RECTANGULAR;

    if (hasRectangularTable(items[0])) {
      if (items[0].table.seatsDirection == TABLE.RECTANGULAR.DIRECTION.ONESIDE) {
        rows.push(items);
      } else {
        rows.push([]);
        for (let i = 0; i < items.length; i++) {
          if (i % 2 === 0) {
            rows[0].push(items[i]);
          } else {
            rows[1].push(items[i]);
          }
        }
      }
    } else {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (rowId !== (('row' in item && item.row.uuid) || ('table' in item && item.table.uuid))) {
          rowId = ('row' in item && item.row.uuid) || ('table' in item && item.table.uuid);

          rows.push([]);
          rowCounter++;
        }

        rows[rowCounter].push(items[i]);
      }
    }

    rows.forEach((item, index) => {
      if (payload.checkerBoardOrder && index % 2 === 0) {
        this.disableSeats(item, payload.spaceBetweenGroup, payload.groupSize, !payload.reverse);
      } else {
        this.disableSeats(item, payload.groupSize, payload.spaceBetweenGroup, payload.reverse);
      }
    });

    this.groupSeats({ payload, rows });
    eventbus.emit('seats:disabled:updated');
  }

  async disableSeats(seats: (Seat | TableSeat)[], groupSize = 1, spaceBetween = 0, reverse = false) {
    let groupSizeCounter = 0;
    let spaceBetweenCounter = 0;

    if (reverse) {
      groupSizeCounter = groupSize;
    }

    seats.forEach((item) => {
      if (groupSizeCounter === groupSize && spaceBetweenCounter === spaceBetween) {
        groupSizeCounter = 0;
        spaceBetweenCounter = 0;
      }

      if (groupSizeCounter < groupSize || spaceBetween === 0) {
        item.enable();
        groupSizeCounter++;
      } else if (spaceBetweenCounter < spaceBetween) {
        item.disable();
        spaceBetweenCounter++;
      }
    });
    this.triggerUnsavedChanges(true);
  }

  groupSeats({ payload, rows }: { payload: Payload; rows: (Seat | TableSeat)[][] }) {
    let newGroupId = this.groupIdCounter;
    if (payload.checkerBoardOrder) {
      let groupSizeCounter = 0;
      let spacesCounter = 0;

      rows.forEach((row, index) => {
        groupSizeCounter = 0;
        spacesCounter = 0;

        if (payload.reverse) {
          newGroupId -= 1;
        }
        if (index % 2 === 0) {
          if (payload.reverse) {
            spacesCounter = payload.groupSize!;
          }

          for (const seat of row) {
            if (groupSizeCounter === payload.spaceBetweenGroup && spacesCounter === payload.groupSize) {
              groupSizeCounter = 0;
              spacesCounter = 0;
              newGroupId++;
            }

            if (spacesCounter < payload.groupSize!) {
              seat.removeFromPreviousGroup();
              spacesCounter++;
            } else if (groupSizeCounter < payload.spaceBetweenGroup!) {
              seat.placeToGroup(newGroupId);
              groupSizeCounter++;
            }
          }
        } else {
          if (payload.reverse) {
            groupSizeCounter = payload.groupSize!;
          }

          for (const seat of row) {
            if (groupSizeCounter === payload.groupSize && spacesCounter === payload.spaceBetweenGroup) {
              groupSizeCounter = 0;
              spacesCounter = 0;
              newGroupId++;
            }
            if (groupSizeCounter < payload.groupSize!) {
              seat.placeToGroup(newGroupId);
              groupSizeCounter++;
            } else if (spacesCounter < payload.spaceBetweenGroup!) {
              seat.removeFromPreviousGroup();
              spacesCounter++;
            }
          }
          newGroupId++;
        }
        newGroupId++;
      });
      this.groupIdCounter = newGroupId;
    } else {
      newGroupId = this.groupIdCounter;

      for (const row of rows) {
        let groupSizeCounter = 0;
        let spacesCounter = 0;

        if (payload.reverse) {
          groupSizeCounter = payload.groupSize!;
          newGroupId -= 1;
        }

        row.forEach((seat) => {
          if (groupSizeCounter === payload.groupSize && spacesCounter === payload.spaceBetweenGroup) {
            groupSizeCounter = 0;
            spacesCounter = 0;
            newGroupId++;
          }

          if (groupSizeCounter < payload.groupSize!) {
            seat.placeToGroup(newGroupId);
            groupSizeCounter++;
          } else if (spacesCounter < payload.spaceBetweenGroup!) {
            seat.removeFromPreviousGroup();
            spacesCounter++;
          }
        });
        newGroupId++;
      }
      this.groupIdCounter = newGroupId;
    }
    this.triggerUnsavedChanges(true);
  }

  onGroupHover(payload: Seat | TableSeat) {
    if (payload.type === 'seat') {
      for (const seat of this.seats) {
        if (seat.grouped) {
          if (seat.attributes._groupId === payload.attributes._groupId) {
            seat.view.node.classList.add('targeted-group');
          }
        }
      }
    } else if (payload.type === 'table_seat') {
      for (const unit of this.units) {
        if ('seats' in unit) {
          for (const seat of unit.seats) {
            if (seat.grouped) {
              if (seat.attributes._groupId === payload.attributes._groupId) {
                seat.view.node.classList.add('targeted-group');
              }
            }
          }
        }
      }
    }
  }

  onGroupLeave(payload: Seat | TableSeat) {
    if (payload.type === 'seat') {
      for (const seat of this.seats) {
        if (seat.grouped) {
          if (seat.attributes._groupId === payload.attributes._groupId) {
            seat.view.node.classList.remove('targeted-group');
          }
        }
      }
    } else if (payload.type === 'table_seat') {
      for (const unit of this.units) {
        if ('seats' in unit) {
          for (const seat of unit.seats) {
            if (seat.grouped) {
              if (seat.attributes._groupId === payload.attributes._groupId) {
                seat.view.node.classList.remove('targeted-group');
              }
            }
          }
        }
      }
    }
  }

  async handleSelectionFinished(area: Section, isMoved: boolean) {
    const selectCount = this.selection.items.length;

    await this.updateSelectedUnits(area, isMoved);

    if (this.selection.items.length === 0) {
      if (RENDERER_MODE && selectCount !== 0) {
        eventbus.emit('selection:updated', this.selection);
      }
      return;
    }
    if (!RENDERER_MODE) {
      if (this.app.commandKeyPressed) {
        this.selection.items.sort(sortUnitsByDOMPosition);
        this.controlSelection();
      } else if (isMoved === false) {
        this.selection.items.sort(sortUnitsByDOMPosition);
        this.selection.purgeAndSelectUnit(this.selection.first!);
        // this is useful to bring the selected units to front
        this.selection.last!.toFront();
        // this.units.filter(u => u.type === TYPE.TEXT).forEach(u => u.view.front());
        // this.units.filter((u) => u.type === TYPE.TEXT || u.type === TYPE.PREDEFINED).forEach((u) => u.view.front());
      }
    }
    this.selection.items.forEach((item) => {
      if (item.type === TYPE.TABLESEAT && item.table.attributes.isGrouped) {
        item.table.seats.forEach((seat) => {
          this.selection.add(seat);
        });
      }
      /*else if (item.type === TYPE.TABLE && item.attributes.isGrouped) {
        item.seats.forEach((seat) => {
          this.selection.add(seat);
        });
      }*/
    });
    if (RENDERER_MODE) {
      //this.selection.items = this.selection.items.filter(u => u.type !== TYPE.TABLE);
    }
    eventbus.emit('selection:updated', this.selection);
    if (!RENDERER_MODE) {
      this.showContextMenu();
    }
  }

  async updateSelectedUnits(area: Section, isMoved: boolean) {
    let units: Unit[];
    const { x, y, width, height } = area.rbox();
    this.svgRect.x = x;
    this.svgRect.y = y;
    this.svgRect.height = height;
    this.svgRect.width = width;
    if (!RENDERER_MODE && this.app.layout === LAYOUT.SECTIONS) {
      units = [...this.sections, ...this.units.filter((unit) => !unit.sid)];
    } else if (!RENDERER_MODE && this.app.layout === LAYOUT.INSIDESECTION) {
      const sid = this.app.currentSection!.uuid.toString().trim();
      units = [...this.seats, ...this.units].filter((seat) => seat.sid && seat.sid.toString().trim() === sid);
    } else if (RENDERER_MODE) {
      units = filter(
        [...this.seats, ...this.units],
        (seat) =>
          seat.type === TYPE.SEAT || seat.type === TYPE.TABLE || seat.type === TYPE.TABLESEAT || seat.type === TYPE.GA
      );
      units = units.filter(
        (u) =>
          !(
            (u.type === TYPE.SEAT && u.row.block.isHidden()) ||
            (u.type === TYPE.TABLESEAT && u.table.isHidden()) ||
            (u.type === TYPE.TABLESEAT && u.isHidden()) ||
            (u.type === TYPE.TABLE && u.isHidden()) ||
            (u.type === TYPE.GA && u.isHidden())
          )
      );
    } else {
      units = [...this.seats, ...this.units];
    }
    let unitsForSelection = this.filterUnitsForMode(units);
    unitsForSelection = sortBy(unitsForSelection, (unit) => unit.type !== TYPE.PREDEFINED);
    if (this.app.props.channel === CHANNEL.REPORTING) unitsForSelection = [];
    if (RENDERER_MODE) {
      await this.checkUnitsForIntersectionRenderer(unitsForSelection);
    } else {
      this.checkUnitsForIntersection(unitsForSelection, isMoved);
    }
  }

  filterUnitsForMode(units: Unit[], mode = this.currentMode) {
    let unitsForSelection: Unit[];
    switch (mode) {
      case MODE.DRAWING:
        unitsForSelection = units;
        break;
      case MODE.LABELS:
        unitsForSelection = units.filter((u) => u.type === TYPE.SEAT || u.type === TYPE.TABLE);
        break;
      case MODE.BLOCKSEATS:
        unitsForSelection = units.filter((u) => u.type === TYPE.SEAT || u.type === TYPE.TABLE || u.type === TYPE.GA);
        break;
      case MODE.POS:
      case MODE.CATEGORIES:
        unitsForSelection = units.filter(
          (u) => u.type === TYPE.SEAT || u.type === TYPE.TABLE || u.type === TYPE.GA || u.type === TYPE.TABLESEAT
        );
        break;
      case MODE.RENDERER:
        unitsForSelection = units.filter(
          (u) => [TYPE.SECTION, TYPE.SEAT, TYPE.TABLE, TYPE.TABLESEAT, TYPE.GA].indexOf(u.type) !== -1
        );
        break;
      case MODE.GROUP:
        unitsForSelection = units.filter((u) => [TYPE.SECTION, TYPE.SEAT, TYPE.TABLE].indexOf(u.type) !== -1);
        break;
      default:
        unitsForSelection = units.filter((u) => [TYPE.SECTION, TYPE.SEAT, TYPE.TABLE, TYPE.GA].indexOf(u.type) !== -1);
        break;
    }
    // choose between selecting the whole tables or table seats
    if (!this.isSelectWholeTables(mode)) {
      const lunitsForSelection: Unit[] = [];
      unitsForSelection.forEach((unit) => {
        if (unit.type === TYPE.TABLE) {
          if (unit.attributes.isGrouped) {
            lunitsForSelection.push(unit);
          }
          unit.seats.forEach((seat) => {
            lunitsForSelection.push(seat);
          });
        } else {
          lunitsForSelection.push(unit);
        }
      });
      unitsForSelection = lunitsForSelection;
    }
    return unitsForSelection;
  }

  static checkIntersection(box: BoxLike, spoint: [number, number]) {
    const points: [number, number][] = [];
    let point: number[] = [];
    point[0] = box.x;
    point[1] = box.y;
    points.push(point as [number, number]);
    point = [];
    point[0] = box.x + box.width;
    point[1] = box.y;
    points.push(point as [number, number]);
    point = [];
    point[0] = box.x + box.width;
    point[1] = box.y + box.height;
    points.push(point as [number, number]);
    point = [];
    point[0] = box.x;
    point[1] = box.y + box.height;
    points.push(point as [number, number]);
    const hasIntersection = Seatmap.isUnitInside(points, spoint);
    return hasIntersection;
  }

  checkUnitsForIntersection(units: Unit[], isMoved: boolean) {
    let stop = false;
    forEach(units, (u) => {
      if (stop && !isMoved) {
        return;
      }
      let hasIntersection = false;
      if (isMoved && this.svgRect.width) {
        const { cx, cy } = u.shape.rbox();
        hasIntersection = Seatmap.checkIntersection(this.svgRect, [cx, cy]);
      } else {
        const { x, y } = this.svgRect;
        hasIntersection = Seatmap.checkIntersection(u.shape.rbox(), [x, y]);
      }
      if (hasIntersection && !u.isSelected) {
        this.selection.toggle(u, hasIntersection);
        stop = true;
        eventbus.emit('seat:selected', u);
      } else if (hasIntersection && u.isSelected) {
        this.selection.toggle(u, hasIntersection);
        stop = true;
        eventbus.emit('seat:deselected', u);
      }
    });
    if (this.currentMode === MODE.CATEGORIES || this.currentMode === MODE.BLOCKSEATS) {
      this.checkGroupedSeatsForInsertion();
    }

    if (this.currentMode === MODE.BLOCKSEATS) {
      this.checkDisabledSeatsForInsertion();
    }
  }

  onSeatLimit(catMaxExceeded = false, limit: number) {
    const lthis = this;
    if (lthis.seatLimitCall) {
      clearTimeout(lthis.seatLimitCall);
    }
    lthis.seatLimitCall = setTimeout(() => {
      lthis.app.props.onSeatLimit(catMaxExceeded, limit);
    }, 200);
  }

  async checkUnitsForIntersectionRenderer(units: Unit[]) {
    let clickedItem: Unit | null = null;

    for (const u of units) {
      let hasIntersection = false;
      const { x, y } = this.svgRect;
      hasIntersection = Seatmap.checkIntersection(u.shape.rbox(), [x, y]) && !u.isHidden();
      /*hasIntersection = Seatmap.checkIntersection(u.shape.rbox(), point) && (
        (u.type === TYPE.SEAT && !u.row.block.isHidden()) ||
        (u.type === TYPE.TABLESEAT && !u.table.isHidden()) ||
        (u.type === TYPE.TABLESEAT && !u.isHidden()) ||
        (u.type === TYPE.TABLE && !u.isHidden()) ||
        (u.type === TYPE.GA && !u.isHidden())
      );*/
      let shouldBeSelected = false;
      if (!(await this.checkSelectionChangingAbility(u, hasIntersection, clickedItem))) {
        continue;
      }

      const max = 'category' in u ? Number(u.category?.max ?? Infinity) : Infinity;
      const seatLimit = Number(this.app.props.seatLimit || Infinity);
      const selectedItemsCnt = this.selection.items.length;

      if (
        u.type !== TYPE.TABLE &&
        !u.isSelected &&
        (selectedItemsCnt >= seatLimit || selectedItemsCnt >= max) &&
        hasIntersection
      ) {
        const catMaxExceeded = selectedItemsCnt >= max;
        let limit = seatLimit;
        if (catMaxExceeded) {
          limit = max;
        }
        this.onSeatLimit(catMaxExceeded, limit);
        continue;
      }
      shouldBeSelected = hasIntersection && !u.isSelected && u.attributes.status !== SEATSTATUS.CART;
      //u.type === TYPE.TABLE ||
      if (u.type === TYPE.TABLESEAT && u.table.attributes.isGrouped) {
        const g = u.type === TYPE.TABLESEAT ? u.table : u;
        const seats = g.seats as (Seat | TableSeat)[];
        if (g.attributes.isGrouped) {
          const lthis = this;
          const fs = first(seats)!;
          shouldBeSelected = hasIntersection && !fs.isSelected;
          if (shouldBeSelected) {
            let remainingSeatCount = seats.length;
            if (this.app.props.seatLimit >= 0) {
              remainingSeatCount = this.app.props.seatLimit - this.selection.items.length;
            }
            if (remainingSeatCount >= seats.length) {
              seats.forEach((s) => {
                lthis.selection.toggle(s, shouldBeSelected);
                eventbus.emit('seat:selected', s);
                clickedItem = u;
              });
            } else {
              this.onSeatLimit(false, this.app.props.seatLimit);
            }
          } else if (fs.isSelected && hasIntersection) {
            seats.forEach((s) => {
              lthis.selection.toggle(s, shouldBeSelected);
              eventbus.emit('seat:deselected', s);
              clickedItem = u;
            });
          }
        }
      } else if (u.type !== TYPE.TABLE && shouldBeSelected) {
        if (u.attributes._groupId !== undefined) {
          if (u.type === TYPE.SEAT) {
            this.seats.forEach((seat) => {
              if (seat.attributes._groupId === u.attributes._groupId) {
                this.selection.toggle(seat, shouldBeSelected);
                eventbus.emit('seat:selected', seat);
              }
            });
          } else if (u.type === TYPE.TABLESEAT) {
            this.units.forEach((unit) => {
              if (unit.type === TYPE.TABLE) {
                for (const seat of unit.seats) {
                  if (seat.attributes._groupId === u.attributes._groupId) {
                    this.selection.toggle(seat, shouldBeSelected);
                    eventbus.emit('seat:selected', seat);
                  }
                }
              }
            });
          }
        }
        this.selection.toggle(u, shouldBeSelected);
        eventbus.emit('seat:selected', u);
        clickedItem = u;
      } else if (u.type !== TYPE.TABLE && hasIntersection && u.isSelected) {
        if (u.attributes._groupId !== undefined) {
          if (u.type === TYPE.SEAT) {
            this.seats.forEach((seat) => {
              if (seat.attributes._groupId === u.attributes._groupId) {
                this.selection.toggle(seat, shouldBeSelected);
                eventbus.emit('seat:deselected', seat);
              }
            });
          } else if (u.type === TYPE.TABLESEAT) {
            this.units.forEach((unit) => {
              if (unit.type === TYPE.TABLE) {
                for (const seat of unit.seats) {
                  if (seat.attributes._groupId === u.attributes._groupId) {
                    this.selection.toggle(seat, shouldBeSelected);
                    eventbus.emit('seat:deselected', seat);
                  }
                }
              }
            });
          }
        }
        this.selection.toggle(u, shouldBeSelected);
        eventbus.emit('seat:deselected', u);
        clickedItem = u;
      }
    }
  }

  checkDisabledSeatsForInsertion() {
    this.selection.items.forEach((selectionSeat) => {
      if (selectionSeat.isSelected && 'disabled' in selectionSeat && selectionSeat.disabled) {
        this.selection.toggle(selectionSeat, false);
        eventbus.emit('seat:deselected', selectionSeat);
      }
    });
  }

  checkGroupedSeatsForInsertion() {
    this.selection.items.forEach((selectionSeat) => {
      if ('grouped' in selectionSeat && selectionSeat.grouped) {
        const currentGroup = this.seatsAndTableSeats.filter(
          (seat) => seat.attributes._groupId === selectionSeat.attributes._groupId
        );

        if (currentGroup.some((seat) => !seat.isSelected)) {
          this.removeGroupedSeatsFromSelection(selectionSeat.attributes._groupId);
        }
      }
    });
  }

  removeGroupedSeatsFromSelection(groupId: number) {
    this.selection.items.forEach((seat) => {
      if (seat.attributes._groupId === groupId && seat.isSelected) {
        this.selection.toggle(seat, false);
        eventbus.emit('seat:deselected', seat);
      }
    });
  }

  /**
   * Returns true if unit should be selected
   * @param {*} u - unit
   * @param {*} hasIntersection - intersection of unit
   */
  async checkSelectionChangingAbility(u: Unit, hasIntersection: boolean, clickedItem: Unit | null) {
    if (!hasIntersection) {
      return false;
    }
    switch (u.type) {
      case TYPE.GA:
        if (!u.seatCanBeSelectedOrDeselected || clickedItem !== null) {
          return false;
        }
        if (this.app.sectionWouldChange(u.sid)) {
          return new Promise<boolean>((resolve) => {
            eventbus.once('sectionAlertResponse', (shouldContinue: boolean) => {
              resolve(shouldContinue);
            });
            eventbus.emit('show:sectionAlert', u);
          });
        }
        //Activate only seats of a specific category.
        if (this.app.categoryFilterationFail(u)) {
          return new Promise<boolean>((resolve) => {
            eventbus.once('categoryAlertResponse', (shouldContinue: boolean) => {
              resolve(shouldContinue);
            });
            eventbus.emit('show:categoryAlert', u);
          });
        }
        if (!u.isSelected) {
          eventbus.emit('menu:hide');
          setTimeout(() => {
            eventbus.emit('menu:show', u);
          });
        } else {
          eventbus.emit('menu:hide');
        }
        return false;
      case TYPE.SECTION:
        //eventbus.emit('camera:goInsideSection', u);
        return true;
      case TYPE.SEAT:
      case TYPE.TABLESEAT: {
        if (!u.seatCanBeSelectedOrDeselected) {
          return false;
        }
        if (u.attributes.invisible === true) {
          return false;
        }

        if (this.app.sectionWouldChange(u.sid)) {
          return new Promise<boolean>((resolve) => {
            eventbus.once('sectionAlertResponse', (shouldContinue: boolean) => {
              resolve(shouldContinue);
            });
            eventbus.emit('show:sectionAlert', u);
          });
        }
        //Activate only seats of a specific category.
        if (this.app.categoryFilterationFail(u)) {
          return new Promise<boolean>((resolve) => {
            eventbus.once('categoryAlertResponse', (shouldContinue: boolean) => {
              resolve(shouldContinue);
            });
            eventbus.emit('show:categoryAlert', u);
          });
        }
        //Activate only seats that are assigned to this POS or not assigned at all.
        if (this.app.posFilterationFail(u)) {
          eventbus.emit('show:posAlert');
          return false;
        }
        //let select groped seats event if single selecting should cause a gap appearance
        if (u.grouped) {
          return true;
        }

        // let checkGapCond = true;
        // const tableSeatGroup = u.type === TYPE.TABLESEAT ? u.table : null;
        // if (tableSeatGroup) {
        //   checkGapCond = !tableSeatGroup.attributes.isGrouped;
        // }

        // if (this.gapEnabled && u.attributes.status === SEATSTATUS.FREE && checkGapCond) {
        //   const gapDetected = checkSeatForGap(u);
        //   if (gapDetected) {
        //     eventbus.emit('show:gapAlert');
        //   }
        //   return !gapDetected;
        // }
        return true;
      }
      default:
        return true;
    }
  }

  isSelectWholeTables(mode = this.currentMode) {
    return mode === MODE.DRAWING || mode === MODE.LABELS;
  }

  selectBlock(block: Block) {
    this.selectUnits(block.seats);
  }

  selectRow(row: Row) {
    this.selectUnits(row.seats);
  }

  selectUnits(units: Unit[] | Unit) {
    units = isArray(units) ? units : [units];
    if (store.state.settings.mode !== MODE.DRAWING) {
      units = reject(
        units,
        (u) =>
          u.attributes.status === SEATSTATUS.OCCUPIED ||
          u.attributes.status === SEATSTATUS.RESERVED ||
          u.attributes.status === SEATSTATUS.CART
      );
    }
    this.clearSelection(units);
    this.showContextMenu();
  }

  showContextMenu() {
    if (this.selection.length > 0) {
      eventbus.emit('menu:show', this.selection);
    }
  }

  hideContextMenu() {
    eventbus.emit('menu:hide');
  }

  getGroupedRows(items: Seat[]) {
    return groupBy(items, (item) => item.row.uuid);
  }

  getGroupedBlocks(items: Seat[]) {
    return groupBy(items, (item) => item.block.uuid);
  }

  clearSelection(newSelection: Unit[] = []) {
    this.hideContextMenu();

    const seats = this.selection.items.filter((u) => u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT);

    forEach(seats, (seat) => {
      eventbus.emit('seat:deselected', seat);
    });

    this.selection.purgeAndSelectUnits(newSelection);

    forEach(
      newSelection.filter((u) => u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT),
      (seat) => {
        eventbus.emit('seat:selected', seat);
      }
    );

    eventbus.emit('selection:updated', this.selection);

    if (this.selection.items.length) {
      nextTick(() => {
        this.showContextMenu();
      });
    }
  }

  controlSelection() {
    if (this.selection.items.length > 0) {
      const funit = this.selection.first!;
      this.bulkSelected.push(funit);
      this.clearSelection(this.bulkSelected);
      this.reInitMenu();
    }
  }

  selectAllUnits() {
    if (this.app.layout === LAYOUT.INSIDESECTION) {
      [...this.seats, ...this.units]
        .filter((u) => u.sid === this.app.currentSection!.uuid)
        .forEach((u) => this.selection.add(u));
    } else if (this.app.layout === LAYOUT.SECTIONS) {
      const units = this.units.filter((unit) => !unit.sid);
      forEach([...this.sections, ...units], (unit) => this.selection.add(unit));
    } else {
      forEach([...this.seats, ...this.units], (unit) => this.selection.add(unit));
    }
    //TODO: recheck if this is needed here, already being called in reInitMenu
    //eventbus.emit('selection:updated', this.selection);
    this.reInitMenu();
  }

  hideSections(sectionExcludedFromHide: Section) {
    forEach(this.sections, (section) => {
      if (section === sectionExcludedFromHide) return;
      section.hide();
    });
    // FIXME: Do we still have that?
    const units = this.units.filter((unit) => !unit.sid); // units outside of sections
    forEach(units, (unit) => unit.hide());
  }

  showSections() {
    forEach(this.sections, (section) => section.show());
    const units = this.units.filter((unit) => !unit.sid);
    forEach(units, (unit) => unit.show());
  }

  enableHotkeys() {
    this.hotkeysDisabled = false;
  }

  disableHotkeys() {
    this.hotkeysDisabled = true;
  }

  isHotkeysDisabled(e: KeyboardEvent) {
    if ((e.target as HTMLElement).nodeName === 'INPUT') return true;
    if (this.app.props.status === EVENTSTATUS.PUBLISHED) return true;
    return this.hotkeysDisabled;
  }

  toJSON() {
    const json = {
      sections: invokeMap(this.sections, 'toJSON'),
      blocks: invokeMap(this.blocks, 'toJSON'),
      units: invokeMap(this.units, 'toJSON'),
      stagepin: this.stagepin?.uuid,
      bgImage: undefined as BgImageState | undefined,
    };
    return json;
  }

  bgJSON() {
    let bgImage: BgImageState | undefined = undefined;
    if (this.app.bgImage && this.app.bgImage.src) {
      const bgImageShowInRenderer = this.bgImageShowInRenderer;
      const bgImageOpacity = this.app.bgImage.opacity();
      const isBgUploaded = this.isBgUploaded;
      const bgImageScale = (this.app.bgImage.transform('scaleX' as any) as unknown as number) / ZOOM.LEVELS.SECTION;
      const x = this.app.bgImage.x();
      const y = this.app.bgImage.y();
      const transform = this.app.bgImage.transform();
      bgImage = { bgImageScale, bgImageOpacity, bgImageShowInRenderer, isBgUploaded, x: x, y: y, transform: transform };
    }
    return bgImage;
  }

  undoHistory() {
    this.pickHistory();
  }

  redoHistory() {
    this.pickHistory(false);
  }

  pickHistory(undo = true) {
    //get current pan and zoom states and section Selection
    this.selectedZone = this.app.currentSection;
    this.currentZoomLevel = this.app.zoomManager.zoom();
    const state = undo === true ? this.history?.undo() : this.history?.redo();
    if (state) {
      if (state.state.viewbox) {
        this.app.svg.viewbox(state.state.viewbox);
      }
      const selectedMode = store.state.settings.mode;
      const showLabels = this.app.showLabels || selectedMode === MODE.LABELS;
      eventbus.emit('seatmap:exitZone');
      let insideZone = false;
      if (this.selectedZone && state.state.sections) {
        state.state.sections.forEach((elem) => {
          if (elem.uuid === this.selectedZone!.uuid) {
            insideZone = true;
          }
        });
      }
      this.initSeatmap(state, true);
      if (insideZone) {
        this.app.currentSection = this.selectedZone;
        store.commit('settings/selection', true);
        this.selectedZone!.addClass('section-shape');
        store.commit('settings/layout', LAYOUT.INSIDESECTION);
        this.app.initEditSectionMode(this.selectedZone!);
        eventbus.emit('seatmap:labels:updated');
        eventbus.emit('camera:zoom', this.currentZoomLevel);
        this.app.svg.viewbox(
          store.state.seatmap.panCoordinate!.x,
          store.state.seatmap.panCoordinate!.y,
          store.state.seatmap.panCoordinate!.width,
          store.state.seatmap.panCoordinate!.height
        );
      }
      store.commit('settings/mode', selectedMode);
      if (showLabels) eventbus.emit('seatmap:labels:show');
      eventbus.emit('history:update', this.history);
      this.clearSelection();
    }
  }

  jumpToHistory(id: string) {
    const state = this.history?.jump(id);
    if (!state) return;

    if (state.state.viewbox) {
      this.app.svg.viewbox(state.state.viewbox);
    }

    const selectedMode = store.state.settings.mode;
    const showLabels = this.app.showLabels || selectedMode === MODE.LABELS;
    eventbus.emit('seatmap:exitZone');
    let insideZone = false;
    if (this.selectedZone && state.state.sections) {
      state.state.sections.forEach((elem) => {
        if (elem.uuid === this.selectedZone!.uuid) {
          insideZone = true;
        }
      });
    }
    this.initSeatmap(state, true);
    if (insideZone) {
      this.app.currentSection = this.selectedZone;
      store.commit('settings/selection', true);
      this.selectedZone!.addClass('section-shape');
      store.commit('settings/layout', LAYOUT.INSIDESECTION);
      this.app.initEditSectionMode(this.selectedZone!);
      eventbus.emit('seatmap:labels:updated');
      eventbus.emit('camera:zoom', this.currentZoomLevel);
    }
    store.commit('settings/mode', selectedMode);
    if (showLabels) eventbus.emit('seatmap:labels:show');

    eventbus.emit('history:update', this.history);
    this.clearSelection();
  }

  triggerUnsavedChanges(state: boolean) {
    store.commit('seatmap/updateUnsavedChanges', !!state);
  }

  snapshot(description: string, onInit = false) {
    //TODO: History is paused for now until we figure out how to implement, but trigger unsaved changes everytime a change is made
    store.commit('seatmap/updateUnsavedChanges', !onInit);
    if (this.history!.paused) return;
    let state = this.toJSON();
    const bgState = this.bgJSON();
    state = { ...state, bgImage: bgState };
    this.history!.snapshot(state, description);
    eventbus.emit('history:update', this.history);
    // @ts-expect-error
    this.app.historyavail = true;
  }

  async loadBgImage(state: any, loadBg = true) {
    if (RENDERER_MODE && !loadBg) return;
    if (this.app.bgImage) return;
    //if (this.app.props.bgImage && state.bgImage) {
    if (this.app.props.bgImage) {
      if (!state.bgImage) state.bgImage = {};
      state.bgImage.bgImage = this.app.props.bgImage;
    }
    // && state.bgImage.bgImage && state.bgImage.bgImageShowInRenderer

    const cond =
      (!RENDERER_MODE && state.bgImage) ||
      (RENDERER_MODE && state.bgImage && state.bgImage.bgImageShowInRenderer !== false);
    if (!cond || !state.bgImage.bgImage) return;

    const bgImage = this.app.canvas.image('').addClass('background-image');
    if (state.bgImage.x && state.bgImage.y) {
      bgImage.x(state.bgImage.x);
      bgImage.y(state.bgImage.y);
    }
    if (state.bgImage.transform) bgImage.transform(state.bgImage.transform);
    if (state.bgImage.bgImageOpacity) bgImage.opacity(state.bgImage.bgImageOpacity);
    const lthis = this;

    return new Promise<void>((resolve) => {
      bgImage
        .load(state.bgImage.bgImage)
        .loaded(function () {
          if (state.bgImage.bgImageScale) {
            const zLevel = ZOOM.LEVELS.SECTION;
            bgImage.scale(state.bgImage.bgImageScale * zLevel, state.bgImage.bgImageScale * zLevel);
          }
          lthis.app.bgImage = bgImage;
          lthis.app.hasBgImage = true;
          store.commit('seatmap/bgImage', state.bgImage.bgImage);
          store.commit('seatmap/isBgUploaded', state.bgImage.isBgUploaded);
          store.commit('seatmap/hasBgImage', true);
          if (state.bgImage.bgImageScale) store.commit('seatmap/bgImageScale', state.bgImage.bgImageScale);
          if (state.bgImage.bgImageOpacity) store.commit('seatmap/bgImageOpacity', state.bgImage.bgImageOpacity);
          eventbus.emit('background:loaded', state.bgImage.bgImage, state.bgImage.isBgUploaded);
          if (store.state.settings.layout === LAYOUT.NOTSELECTED) {
            if (lthis.app.grid) lthis.app.grid.addClass('grid_hidden');
            if (lthis.app.bgImage) lthis.app.bgImage.addClass('bg_hidden');
          }
          if (!state.bgImage.x || !state.bgImage.y) {
            bgImage.center(0, 0);
          }

          bgImage.back();
          resolve();
        })
        .error(() => resolve());
    });
  }

  initSeatmap({ state }: { state: State }, seatmapReinit = false, orderInfo = []) {
    this.state = state;

    this.history?.pause();
    this.destroyAllUnits();
    if (RENDERER_MODE) this.orders = orderInfo;
    if (seatmapReinit) {
      this.blocks = [];
      this.seats = [];
      this.units = [];
      this.sections = [];
    }
    return this.loadBgImage(this.state, !seatmapReinit).then(() => {
      const { sections, blocks, units } = this.state!;
      forEach(sections, (sectionParams) => {
        const entity = fromJSON(sectionParams);
        this.addUnit(entity, true);
        // const color = entity.attributes.color;
        // const alpha = parseFloat(this.color.replace(/^.*,(.+)\)/, '$1'));
        // if (alpha <= 0.02) {
        //   entity.view.attr({
        //     stroke: this.color,
        //   });
        // }
      });
      forEach(blocks, (block) => {
        // const MIN = { x: Infinity, y: Infinity };
        // const MAX = { x: -Infinity, y: -Infinity };
        // const { rows } = block;
        // forEach(rows, (row) => {
        //   const { seats } = row;
        //   forEach(seats, (seat) => {
        //     const [x, y] = seat.attributes.position;
        //     if (MIN.x > x) MIN.x = x;
        //     if (MIN.y > y) MIN.y = y;
        //     if (MAX.x < x) MAX.x = x;
        //     if (MAX.y < y) MAX.y = y;
        //   });
        // });
        // block.MIN = MIN;
        // block.MAX = MAX;
        const entity = fromJSON(block);
        this.addBlock(entity, false, true);

        if (this.settings.layout !== LAYOUT.SECTIONS) {
          entity.show();
        }
      });
      forEach(units, (unit) => {
        // if (RENDERER_MODE) {
        //   let MIN;
        //   let MAX;
        //   if (unit.attributes.radius) {
        //     MIN = {
        //       x: unit.attributes.position[0] - unit.attributes.radius,
        //       y: unit.attributes.position[1] - unit.attributes.radius,
        //     };
        //     MAX = {
        //       x: unit.attributes.position[0] + unit.attributes.radius,
        //       y: unit.attributes.position[1] + unit.attributes.radius,
        //     };
        //   } else {
        //     MIN = {
        //       x: unit.attributes.position[0],
        //       y: unit.attributes.position[1],
        //     };
        //     MAX = {
        //       x: unit.attributes.position[0] + (unit.attributes.width ? unit.attributes.width : 0),
        //       y: unit.attributes.position[1] + (unit.attributes.height ? unit.attributes.height : 0),
        //     };
        //   }
        //   unit.MIN = MIN;
        //   unit.MAX = MAX;
        // } else {
        const entity = fromJSON(unit);
        this.addUnit(entity, true);
        // }
      });
      this.history?.resume();
      this.sections.forEach((s) => {
        s.hideSectionUnits();
      });

      this.stagepin = this.units.find((u) => u.uuid === state.stagepin) as PredefinedShape;

      eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
      if (RENDERER_MODE) {
        this.stagepin?.view.style('visibility', 'hidden');

        if (this.app.props.categoryID) {
          this.sections.forEach((s) => {
            s.markCurrentCategoryZones();
          });
        }
        // not sure : remove this and call on fit svg
        if (!seatmapReinit) {
          eventbus.emit('renderer:addPreselectedSeats');
        }
        createCompas(this.app);
      }
      this.app.zoomManager.centerizeAndInitializeZoomLevels(seatmapReinit);
    });
  }

  onRemoveBlock(block: Block) {
    this.blocks = without(this.blocks, block);
    this.snapshot('Block removed');
  }

  onRemoveSeat(seat: Seat) {
    this.seats = without(this.seats, seat);
  }

  onRemoveSection(section: Section) {
    this.unitsFromSection(section.uuid).forEach((u) => u.destroy());
    this.sections = without(this.sections, section);
  }

  onRemoveUnit(unit: Unit) {
    this.units = without(this.units, unit);
  }
  removeUnits(units: Unit[]) {
    this.units = without(this.units, ...units);
  }

  removeSelectedUnits() {
    if (this.selection.length === 0) return;
    this.history?.pause();

    const seats = this.selection.seats;
    const wholeBlockSelected = Object.values(groupBy(seats, (unit) => unit.block.uuid)).every(
      (group) => group.length === group[0].block.seats.length
    );

    if (wholeBlockSelected) {
      forEach(seats, (unit) => unit.destroy());
    } else {
      // only hide seats instead of destryoing them
      seats?.forEach((seat) => seat.makeInvisible());
    }

    const sections = this.selection.sections;
    sections.forEach((section) => {
      const seats = this.seats.filter((seat) => seat.sid === section.uuid);
      if (seats.some((seat) => seat.attributes.status === SEATSTATUS.CART)) {
        return;
      }

      section.destroy();
    });

    const selection = reject(this.selection.items, (unit) => unit.type === TYPE.SEAT || unit.type === TYPE.SECTION);

    forEach(selection, (unit) => unit.destroy());
    this.clearSelection(wholeBlockSelected ? undefined : seats);
    eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
    eventbus.emit('update:invisibleSeats');
    this.history?.resume();
    this.snapshot('Removed units');
    this.triggerUnsavedChanges(true);
  }

  removeBlocks(seats = this.selection.seats) {
    this.hideContextMenu();
    this.selection.purge();

    const groupedSeats = groupBy(seats, (seat) => seat.block.uuid);

    Object.values(groupedSeats).forEach((seats) => {
      seats[0].block.destroy();
    });

    eventbus.emit('selection:updated', this.selection);
  }

  showLockOnSeats() {
    this.app.getFilteredSeats(this.app.currentSection?.uuid).forEach((u) => u.showLock());
  }

  hideLockOnSeats() {
    this.app.getFilteredSeats(this.app.currentSection?.uuid).forEach((u) => u.hideLock());
  }

  showLabels() {
    this.app.getFilteredSeats(this.app.currentSection?.uuid).forEach((u) => u.showLabel());
  }

  hideLabels() {
    this.app.getFilteredSeats(this.app.currentSection?.uuid).forEach((u) => u.hideLabel());
  }

  /* section related */
  showSectionUnits(sid: string) {
    this.unitsFromSection(sid)
      .filter((u) => u.sid === sid)
      .forEach((u) => u.show());
  }

  hideSectionUnits(sid: string) {
    if (RENDERER_MODE) {
      this.sections.find((s) => s.uuid === sid)!.view.addClass('outside-view');
    }
    this.unitsFromSection(sid)
      .filter((u) => u.sid === sid)
      .forEach((u) => u.hide());
  }

  lockPositionsAndShowInFront(sid: string) {
    this.unitsFromSection(sid)
      .filter((u) => u.sid === sid)
      .forEach((u) => {
        if (u.type === TYPE.BLOCK) {
          u.seats.forEach((s) => {
            s.view.front();
            s.attributes.canBeDraggable = false;
          });
        }
        u.show();
        u.view.front();
        u.attributes.canBeDraggable = false;
      });
  }

  unlockPositionsAndHideInBack(sid: string) {
    this.unitsFromSection(sid)
      .filter((u) => u.sid === sid)
      .forEach((u) => {
        if (u.type === TYPE.BLOCK) {
          u.seats.forEach((s) => {
            s.attributes.canBeDraggable = true;
          });
        }
        u.hide();
        u.attributes.canBeDraggable = true;
      });
  }

  assignCategoryToSection(section: Section, categoryId: number) {
    section.categoryId = categoryId;
    filterBySid<Seat | Table>(this.seatsAndTables, section.uuid).forEach((u) => u.assignCategory(categoryId));
    eventbus.emit('refreshUnassignedCount');
  }

  // unassignCategoryFromSection(sid) {
  //   this.seatsAndTables.filter(u => u.sid === sid).forEach(u => u.unassignCategory());
  // }

  assignPosToSection(sid: string, pos: POS) {
    this.seatsAndTablesAndGa.filter((u): u is Seat | Table => u.sid === sid).forEach((u) => u.assignPos(pos));
  }

  unassignPosFromSection(sid: string) {
    this.seatsAndTablesAndGa.filter((u): u is Seat | Table => u.sid === sid).forEach((u) => u.unassignPos());
  }

  onSectionDragStart(sid: string) {
    this.unitsFromSection(sid).forEach((u) => u.onDragStart());
  }

  onSectionDragEnd(sid: string, dx: number, dy: number, dragged: boolean) {
    this.unitsFromSection(sid).forEach((u) => {
      if (dragged) u.onDragMove(dx, dy);
      u.onDragEnd();
    });
  }

  onSectionRotateStart(sid: string) {
    this.unitsFromSection(sid).forEach((u) => u.onRotateStart());
  }

  onSectionRotateEnd(sid: string, rm: SVG.Matrix, angle: number) {
    this.unitsFromSection(sid).forEach((u) => {
      u.onRotateMove(rm);
      u.onRotateEnd();
    });
  }

  onSectionUnFill(section: Section) {
    if (RENDERER_MODE) {
      this.sections
        .find((s) => s.uuid === section.uuid)!
        .view.attr({
          fill: SECTION.TRANSPARENTFILLCOLOR,
        });
    }
  }

  onSectionFill(section: Section) {
    if (RENDERER_MODE) {
      const color = section.attributes.color;
      this.sections
        .find((s) => s.uuid === section.uuid)!
        .view.attr({
          fill: color,
        });
    }
  }

  /* end of section related */

  destroyAllUnits() {
    if (this.app.layout === LAYOUT.SECTIONS || this.app.layout === LAYOUT.INSIDESECTION) {
      this.units.forEach((u) => u.destroy(true));
      this.sections.forEach((s) => s.destroy(true));
    } else {
      [...this.blocks, ...this.units].forEach((u) => u.destroy(true));
    }
  }

  reset() {
    this.pauseHistory();
    this.clearSelection();
    this.destroyAllUnits();
    eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
    this.destroy();
    this.initHistory();
    this.addEventListeners();
    eventbus.emit('camera:pan:center');
  }

  destroyHistory() {
    this.history?.destroy();
    delete this.history;
  }

  initHistory() {
    this.history = new History(this);
  }

  destroy() {
    this.destroyHistory();
    this.removeEventListeners();
  }

  /**
   * Update Visiblity of objects when camera changed
   */
  updateVisibility() {
    const viewbox = new SVG.Box(this.app.svg.viewbox());
    this.sections.forEach((section) => {
      if (section.isInside) {
        section.showSectionUnits();
      }
    });

    this.units.forEach((unit) => {
      const [x, y] = unit.attributes.position;
      if (viewbox.x > x || viewbox.x2 < x || viewbox.y > y || viewbox.y2 < y) {
        unit.hide();
      } else {
        unit.show();
      }
    });

    this.sections.forEach((section) => {
      if (!section.isInside) {
        section.hideSectionUnits();
      }
    });
  }

  onScaleChange(scale: number) {
    const oldScale = this.scale;
    this.scale = scale;

    Object.values(this.getGroupedBlocks(this.seats)).forEach((seats) => {
      // calculate the center of the block
      const { cx, cy } = seats[0].row.block.view.bbox();

      seats.forEach((u) => u.onScaleChange(scale / oldScale, cx, cy));
    });

    Object.values(this.getGroupedRows(this.seats)).forEach((seats) => {
      seats[0].row.onScaleChange();
    });

    this.tablesAndGa.forEach((u) => u.onScaleChange(scale / oldScale));

    eventbus.emit('row:renderLabel');
    eventbus.emit('selection:updated', this.selection);
  }

  addSelectedSeatsToCart() {
    const seats = this.selection.items.filter(
      (u): u is Seat | TableSeat => u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT
    );
    if (seats.length) {
      seats.forEach((seat) => {
        seat.addToCart();
      });

      const oldSelection = this.selection.items.filter((u) => u.type !== TYPE.SEAT && u.type !== TYPE.TABLESEAT);
      this.clearSelection(oldSelection);
    }
  }

  deleteSectionUnits(sectionId: string) {
    this.unitsFromSection(sectionId).forEach((u) => u.destroy());
    eventbus.emit('seatmap:updated', this.seats, this.units, this.sections);
  }

  startSeatTransfer(sid: string) {
    if (!this.store.splitModeUnit) return;

    const source = this.sections.find((s) => s.uuid === this.store.splitModeUnit)!;
    const target = this.sections.find((s) => s.uuid === sid)!;

    /****************************************
     * Copying seats over to the new section
     ****************************************/

    const seats = this.seats.filter((u) => u.sid === this.store.splitModeUnit);
    const seatsInBBox = getUnitsInSection(target, seats) as Seat[];

    // group by block
    const groupedByBlock = this.getGroupedBlocks(seatsInBBox);

    Object.values(groupedByBlock).forEach((seats) => {
      const rows = Object.values(groupBy(seats, (s) => s.row.uuid));

      const block = Block.create({ sid });
      rows.forEach((r) => {
        const row = Row.create({ block, attributes: r[0].row.attributes });
        r.forEach((seat) => {
          const { attributes, uuid } = seat;

          Seat.create({
            row,
            attributes,
            uuid,
          });
        });
        row.renderLabel(false);
      });
      block.calculateLengthsCoeffs();
      eventbus.emit('seatmap:add:block', block);
    });

    const { label, opacity, scale, entrance, level, sectionCode } = source.attributes;
    const { stroke, text, size } = label;
    Object.assign(target.attributes, {
      opacity,
      scale,
      entrance,
      level,
      sectionCode,
      label: { stroke, text, size, hOffset: 0, vOffset: 0, labeltransform: '', rotation: 0 },
    });

    if (text !== undefined) {
      target.changeLabel(text);
    }
    if (stroke !== undefined) {
      target.changeLabelStroke(stroke, false);
    }
    if (opacity !== undefined) {
      target.changeOpacity(opacity, false);
    }
    if (size !== undefined) {
      target.changeFontSize(size);
    }

    if (target.subtype === TYPE.POLYGON) {
      target.attributes.smoothing = 0;
      (target as unknown as PolygonalSection).plot();
    }

    seatsInBBox.forEach((seat) => {
      seat.destroy(true);
    });

    /****************************************
     * Copying tabls and tableseats over to the new section
     ****************************************/

    const tables = this.tables.filter((u) => u.sid === this.store.splitModeUnit);
    const tablesInBBox = getUnitsInSection(target, tables) as Table[];

    tablesInBBox.forEach((table) => {
      const seats = table.seats.map((seat) => {
        return TableSeat.create({ ...seat, sid, attributes: { ...seat.attributes } });
      });

      const Type = table.subtype === TYPE.TABLE_RECTANGULAR ? RectangularTable : RoundedTable;

      const newTable = new Type({
        ...table,
        sid,
        attributes: { ...table.attributes },
        seats,
      });
      eventbus.emit('seatmap:add:unit', newTable, false);

      table.destroy();
    });

    /****************************************
     * assigning category to seats and tables
     ****************************************/
    target.assignCategory(this.app.props.sections ? null : source.categoryId);

    /****************************************
     * Copying over other units
     * (e.g. icons, shapes, text, etc.)
     ****************************************/

    const units = this.units.filter((u) => u.sid === this.store.splitModeUnit && u.type !== TYPE.TABLE);
    const unitsInBBox = getUnitsInSection(target, units);

    unitsInBBox.forEach((unit) => {
      let newUnit = null;
      if (unit.type === TYPE.PREDEFINED) {
        newUnit = PredefinedShape.create({
          ...unit,
          sid,
          attributes: { ...unit.attributes },
        });
      } else if (unit.type === TYPE.SHAPE) {
        // FIXME: unit as Rect is obvisouly wrong. Shape should be a union instead of a class
        newUnit = ShapeHelper.create((unit as Rect).subtype, {
          ...unit,
          sid,
          attributes: { ...unit.attributes },
        });
      } else if (unit.type === TYPE.TEXT) {
        newUnit = Text.create({
          ...unit,
          sid,
          attributes: { ...unit.attributes },
        });
      }

      if (newUnit) {
        eventbus.emit('seatmap:add:unit', newUnit, false);
      }

      unit.destroy();
    });

    target.hideInsides();

    this.selection.purge();
    eventbus.emit('selection:updated', this.selection);

    if (
      seats.length === seatsInBBox.length &&
      tables.length === tablesInBBox.length &&
      units.length === unitsInBBox.length
    ) {
      source.destroy();
      this.actions.disableSplitMode();
    }
  }

  splitBlock(groupedRows: { [key: string]: Seat[] }) {
    const rowArray = Object.values(groupedRows);
    const block = rowArray[0][0].block;

    const fullRowsSelected = rowArray.every((seats) => {
      return seats[0].row.seats.length === seats.length;
    });

    if (fullRowsSelected) {
      const rowIndexes = rowArray.map((row) => block.rows.indexOf(row[0].row));
      const startIndex = Math.min(...rowIndexes);
      const endIndex = Math.max(...rowIndexes);

      const block1Rows = block.rows.slice(0, startIndex);
      const block2Rows = block.rows.slice(startIndex, endIndex + 1);
      const block3Rows = block.rows.slice(endIndex + 1);

      this.createBlockWithSeatsByRow(block, block1Rows);
      this.createBlockWithSeatsByRow(block, block2Rows);
      this.createBlockWithSeatsByRow(block, block3Rows);
    } else {
      const seatData = block.seats.map((seat) => {
        return {
          seat,
          colIndex: seat.row.seats.indexOf(seat),
          rowIndex: seat.block.rows.indexOf(seat.row),
        };
      });

      const selectedSeats = rowArray.reduce((acc, seats) => {
        return [...acc, ...seats];
      }, []);

      const selectedSeatsData = selectedSeats.map((seat) => {
        return {
          seat,
          colIndex: seat.row.seats.indexOf(seat),
          rowIndex: seat.block.rows.indexOf(seat.row),
        };
      });

      const colIndexes = selectedSeatsData.map((seat) => seat.colIndex);
      const startIndex = Math.min(...colIndexes);
      const endIndex = Math.max(...colIndexes);

      const block1Seats = seatData.filter((seat) => seat.colIndex < startIndex);
      const block2Seats = selectedSeatsData;
      const block3Seats = seatData.filter((seat) => seat.colIndex > endIndex);

      this.createBlockWithSeatsByColumn(block, block1Seats);
      this.createBlockWithSeatsByColumn(block, block2Seats);
      this.createBlockWithSeatsByColumn(block, block3Seats);
    }

    this.selection.purge();
    eventbus.emit('selection:updated', this.selection);
    block.destroy();
  }

  createBlockWithSeatsByColumn(
    source: Block,
    seats: {
      seat: Seat;
      colIndex: number;
      rowIndex: number;
    }[]
  ) {
    if (!seats.length) return;
    const block = Block.create({ sid: source.sid });
    Object.values(groupBy(seats, (seat) => seat.rowIndex)).forEach((seats) => {
      const row = Row.create({ block, attributes: seats[0].seat.row.attributes });
      seats.forEach((seat) => {
        Seat.create({ row, attributes: seat.seat.attributes });
      });
      row.renderLabel(false);
    });
    block.calculateLengthsCoeffs();
    eventbus.emit('seatmap:add:block', block);
    block.show();
  }

  createBlockWithSeatsByRow(source: Block, seats: Row[]) {
    if (!seats.length) return;
    const block = Block.create({ sid: source.sid });
    seats.forEach((row) => {
      const newRow = Row.create({ block, attributes: row.attributes });
      row.seats.forEach((seat) => {
        Seat.create({ row: newRow, attributes: seat.attributes });
      });
      newRow.renderLabel(false);
    });

    block.calculateLengthsCoeffs();
    eventbus.emit('seatmap:add:block', block);
    block.show();
  }

  getVisibleSeats(sid?: string) {
    let { seatsAndTableSeats } = this;
    if (sid) {
      seatsAndTableSeats = seatsAndTableSeats.filter(
        (seat) => (('table' in seat && seat.table.sid) || seat.sid) === sid
      );
    }

    return seatsAndTableSeats.filter((s) => s.attributes.invisible !== true);
  }

  updateSeatVisibility(visible: boolean) {
    if (this.settings.mode === MODE.LABELS || visible) {
      this.app.svg.removeClass('hide-invisible-seats');
    } else {
      this.app.svg.addClass('hide-invisible-seats');
    }
  }
}
